import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { formatGameTime } from "@/features/analysis";

import { useDrillAssets } from "../../api/useDrillAssets";
import type { DrillSpec } from "../../model/Drill";

const BUILD_ORDER_HINT = msg({
  message: "As strong players on {hero} build them: first goes what they buy earliest.",
});
const BUY_MINUTE = msg({ message: "By what minute do strong players on {hero} buy {item}?" });
const ODD_ITEM = msg({
  message: "Which of these items do strong players on {hero} almost never buy by {time}?",
});
const COUNTER_PAIRS = msg({
  message: "Match each enemy with what strong players on {hero} take against them",
});
const KILLER_COUNTERS = msg({ message: "What do strong players on {hero} take against {killer}?" });
const KILLER_DEFENSE = msg({ message: "How do you defend against {killer}?" });
const KILLER_DEFENSE_HINT = msg({
  message:
    "Builds don't change against him, his damage is {kind, select, bullet {bullets} spirit {spirit} other {melee}}.",
});
const FINISH_HIM = msg({ message: "How do you catch {enemy}?" });
// Лишняя покупка — редкая у сильных или купленная намного раньше их (`off_build_early`):
// вопрос подходит к обоим случаям.
const PURCHASE_MISTAKE = msg({
  message: "Which purchase don't strong players on {hero} make at this point?",
});
const ANTI_HEAL = msg({ message: "Which anti-heal do strong players on {hero} take?" });
// С этой версии генераторов «Антихил» — из одних антихилов: вопрос, какой подходит герою.
const ANTI_HEAL_BY_HERO_VERSION = 3;
const FIRST_BUFF = msg({
  message: "When do strong players on {hero} take their first permanent buff?",
});

export type DrillQuestion = { title: string; hint: string | null };

// Вопрос задачи и подсказка под ним — по виду задачи и её числам.
export const useDrillQuestion = (): ((spec: DrillSpec) => DrillQuestion) => {
  const { i18n, t } = useLingui();
  const assets = useDrillAssets();
  return (spec) => {
    const hero = assets.hero(spec.hero_id).name;
    const target = spec.target_hero_id == null ? "" : assets.hero(spec.target_hero_id).name;
    const time = formatGameTime(spec.moment_s ?? 0);
    switch (spec.kind) {
      case "buy_now":
        return { title: t`What will you buy now?`, hint: null };
      case "build_order":
        return {
          title: t`Put them in purchase order`,
          hint: i18n._({ ...BUILD_ORDER_HINT, values: { hero } }),
        };
      case "buy_minute":
        return {
          title: i18n._({
            ...BUY_MINUTE,
            values: { hero, item: assets.item(spec.target_item_id ?? 0).name },
          }),
          hint: null,
        };
      case "odd_item":
        return { title: i18n._({ ...ODD_ITEM, values: { hero, time } }), hint: null };
      case "purchase_mistake":
        return {
          title: i18n._({ ...PURCHASE_MISTAKE, values: { hero } }),
          hint:
            spec.references.length > 0
              ? t`Compare with what they usually buy at this point. Tap the purchase on the timeline.`
              : t`Tap it on your match timeline.`,
        };
      case "anti_heal":
        return spec.version >= ANTI_HEAL_BY_HERO_VERSION
          ? { title: i18n._({ ...ANTI_HEAL, values: { hero } }), hint: t`All of them cut healing.` }
          : { title: t`What will cut their healing?`, hint: null };
      case "defense_item":
        return { title: t`What will you buy for defense?`, hint: null };
      case "counter_pairs":
        return {
          title: i18n._({ ...COUNTER_PAIRS, values: { hero } }),
          hint: t`Tap an enemy, then an item.`,
        };
      case "killer_counters":
        // Без контрпредметов задача спрашивает защиту от его урона (`damage_kind`).
        return spec.damage_kind == null
          ? {
              title: i18n._({ ...KILLER_COUNTERS, values: { hero, killer: target } }),
              hint: spec.mechanic === "multi" ? t`There can be several right answers.` : null,
            }
          : {
              title: i18n._({ ...KILLER_DEFENSE, values: { killer: target } }),
              hint: i18n._({ ...KILLER_DEFENSE_HINT, values: { kind: spec.damage_kind } }),
            };
      case "finish_him":
        return {
          title: i18n._({ ...FINISH_HIM, values: { enemy: target } }),
          hint: spec.mechanic === "multi" ? t`There can be several right answers.` : null,
        };
      case "first_buff":
        return { title: i18n._({ ...FIRST_BUFF, values: { hero } }), hint: null };
    }
  };
};
